import React, { useState, useEffect } from "react";
import axios from "axios";
import { PlusIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useConfirm } from "../contexts/ConfirmContext";

const Vision = () => {
  const { confirm } = useConfirm();
  const [visions, setVisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedVision, setSelectedVision] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "mission",
    year: new Date().getFullYear(),
    isActive: true,
  });

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

  useEffect(() => {
    fetchVisions();
  }, []);

  const fetchVisions = async () => {
    try {
      const response = await axios.get(`${API_URL}/visions`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setVisions(response.data);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch visions");
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedVision) {
        await axios.put(`${API_URL}/visions/${selectedVision.id}`, formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      } else {
        await axios.post(`${API_URL}/visions`, formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      }
      setShowAddModal(false);
      setSelectedVision(null);
      setFormData({
        title: "",
        description: "",
        type: "mission",
        year: new Date().getFullYear(),
        isActive: true,
      });
      fetchVisions();
    } catch (err) {
      setError("Failed to save vision");
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Delete vision",
      message: "Are you sure you want to delete this vision?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API_URL}/visions/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      fetchVisions();
    } catch (err) {
      setError("Failed to delete vision");
    }
  };

  const handleEdit = (vision) => {
    setSelectedVision(vision);
    setFormData({
      title: vision.title,
      description: vision.description,
      type: vision.type,
      year: vision.year,
      isActive: vision.isActive,
    });
    setShowAddModal(true);
  };

  const filteredVisions = visions.filter(
    (vision) =>
      vision.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vision.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row flex-grow h-full bg-white rounded-lg shadow overflow-hidden">
      {/* Left Column - Vision List */}
      <div className="w-full lg:w-1/3 border-b lg:border-b-0 lg:border-r border-gray-200 overflow-y-auto">
        <div className="px-4 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <div className="flex items-center space-x-4 w-full sm:w-auto">
              {/* Search Input */}
              <div className="relative w-48">
                <input
                  type="text"
                  placeholder="Search visions"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
                <svg
                  className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 transform -translate-y-1/2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              {/* Add Vision Button */}
              <button
                onClick={() => {
                  setSelectedVision(null);
                  setFormData({
                    title: "",
                    description: "",
                    type: "mission",
                    year: new Date().getFullYear(),
                    isActive: true,
                  });
                  setShowAddModal(true);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <PlusIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {filteredVisions.map((vision) => (
              <div
                key={vision.id}
                className={`flex items-center p-3 sm:p-4 rounded-lg cursor-pointer ${
                  selectedVision?.id === vision.id
                    ? "bg-indigo-50 border border-indigo-200"
                    : "hover:bg-gray-50"
                }`}
                onClick={() => setSelectedVision(vision)}
              >
                <div className="ml-3 sm:ml-4 flex-grow min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 truncate">
                    {vision.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500">
                    {vision.type} - {vision.year}
                  </p>
                  <div className="mt-1 flex items-center">
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full ${
                        vision.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {vision.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(vision);
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(vision.id);
                    }}
                    className="text-red-500 hover:text-red-700"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Column - Vision Details */}
      <div className="w-full lg:w-2/3 overflow-y-auto p-4 sm:p-6">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {selectedVision ? (
          <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 break-words">
                {selectedVision.title}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Vision Information
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <dl className="space-y-4">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Type
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {selectedVision.type}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Year
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {selectedVision.year}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Status
                        </dt>
                        <dd className="mt-1">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              selectedVision.isActive
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {selectedVision.isActive ? "Active" : "Inactive"}
                          </span>
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Description
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">
                      {selectedVision.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500">Select a vision to view details</p>
          </div>
        )}
      </div>

      {/* Add/Edit Vision Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium mb-4">
              {selectedVision ? "Edit Vision" : "Add New Vision"}
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    required
                    value={formData.title}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Type
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    <option value="mission">Mission</option>
                    <option value="vision">Vision</option>
                    <option value="goal">Goal</option>
                    <option value="objective">Objective</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Year
                  </label>
                  <input
                    type="number"
                    name="year"
                    value={formData.year}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedVision(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
                >
                  {selectedVision ? "Update" : "Add"} Vision
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vision;
